import React from 'react';
// === LOGO MARK ===
// Stylized D + bladder/shield monogram
export function LogoMark({
  className = '',
  color = 'currentColor'
}: {
  className?: string;
  color?: string;
}) {
  return <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true" data-id="element-249">
      {/* Outer shield/drop */}
      <path d="M16 2 C 22 6, 28 12, 28 19 C 28 25, 22 30, 16 30 C 10 30, 4 25, 4 19 C 4 12, 10 6, 16 2 Z" stroke={color} strokeWidth="1.5" fill="none" data-id="element-250" />
      {/* Inner D shape */}
      <path d="M11 10 L 11 22 L 17 22 C 21 22, 23 19, 23 16 C 23 13, 21 10, 17 10 Z" fill={color} data-id="element-251" />
      {/* Small accent dot */}
      <circle cx="16" cy="6" r="1" fill={color} data-id="element-252" />
    </svg>;
}
// === SECTION ORNAMENT ===
// Editorial-style flourish for use as a section divider
export function Ornament({
  className = '',
  color = '#C9A96E',
  width = 80
}: {
  className?: string;
  color?: string;
  width?: number;
}) {
  return <svg width={width} height={width * 0.2} viewBox="0 0 100 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true" data-id="element-253">
      <line x1="0" y1="10" x2="38" y2="10" stroke={color} strokeWidth="1" data-id="element-254" />
      <line x1="62" y1="10" x2="100" y2="10" stroke={color} strokeWidth="1" data-id="element-255" />
      <path d="M50 4 L 54 10 L 50 16 L 46 10 Z" fill={color} data-id="element-256" />
      <circle cx="42" cy="10" r="1" fill={color} data-id="element-257" />
      <circle cx="58" cy="10" r="1" fill={color} data-id="element-258" />
    </svg>;
}
// === DECORATIVE QUOTE MARK ===
export function QuoteMark({
  className = '',
  color = '#C9A96E'
}: {
  className?: string;
  color?: string;
}) {
  return <svg viewBox="0 0 40 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true" data-id="element-259">
      <path d="M0 18 C 0 8, 6 2, 14 0 L 14 4 C 9 6, 6 11, 6 16 L 14 16 L 14 28 L 2 28 C 0.5 28, 0 27, 0 25 Z M 22 18 C 22 8, 28 2, 36 0 L 36 4 C 31 6, 28 11, 28 16 L 36 16 L 36 28 L 24 28 C 22.5 28, 22 27, 22 25 Z" fill={color} data-id="element-260" />
    </svg>;
}
// === DOCTOR SIGNATURE ===
// A handwritten-style scribble approximating "Dr. S Mitchell"
export function Signature({
  className = '',
  color = '#1A1A1A'
}: {
  className?: string;
  color?: string;
}) {
  return <svg viewBox="0 0 200 60" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true" data-id="element-261">
      <path d="M 6 38 C 10 20, 18 16, 22 28 C 24 36, 18 42, 14 38 M 28 32 C 30 22, 34 22, 32 36 C 31 42, 36 32, 40 26 M 44 40 C 46 30, 50 26, 52 36 M 50 22 L 50 22 M 60 24 C 56 30, 56 40, 64 40 C 72 40, 72 28, 64 28 C 60 28, 58 32, 60 38" stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-262" />
      <path d="M 84 16 C 80 26, 80 40, 86 40 C 92 40, 92 26, 86 16 M 100 30 C 96 30, 96 40, 102 40 C 108 40, 110 30, 106 26 C 102 22, 98 26, 100 32 M 116 18 L 116 42 M 116 30 C 122 24, 128 28, 124 36 C 120 42, 116 38, 116 36 M 134 20 L 134 42 M 134 30 C 140 24, 146 26, 144 34 M 154 30 C 152 30, 152 40, 156 40 C 162 40, 164 30, 160 26 C 156 22, 152 26, 154 32 M 172 18 L 172 42 M 172 30 C 178 24, 184 26, 184 36 M 190 30 L 196 30" stroke={color} strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-263" />
    </svg>;
}
// === ANATOMY DIAGRAM ===
// Simplified, editorial illustration: pelvic region with nerve signal pathway
export function AnatomyDiagram({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 400 260" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true" data-id="element-264">
      {/* Background panel */}
      <rect x="0" y="0" width="400" height="260" fill="#FAFAF8" rx="8" data-id="element-265" />

      {/* Subtle grid */}
      <g opacity="0.15" data-id="element-266">
        <line x1="0" y1="65" x2="400" y2="65" stroke="#C9A96E" strokeWidth="0.5" strokeDasharray="2 4" data-id="element-267" />
        <line x1="0" y1="130" x2="400" y2="130" stroke="#C9A96E" strokeWidth="0.5" strokeDasharray="2 4" data-id="element-268" />
        <line x1="0" y1="195" x2="400" y2="195" stroke="#C9A96E" strokeWidth="0.5" strokeDasharray="2 4" data-id="element-269" />
      </g>

      {/* Brain (top) */}
      <g transform="translate(80, 35)" data-id="element-270">
        <ellipse cx="0" cy="0" rx="32" ry="22" fill="none" stroke="#1A1A1A" strokeWidth="1.5" data-id="element-271" />
        <path d="M -25 -5 Q -15 -15, 0 -10 Q 15 -15, 25 -5" fill="none" stroke="#1A1A1A" strokeWidth="1" data-id="element-272" />
        <path d="M -20 5 Q -10 0, 0 5 Q 10 0, 20 5" fill="none" stroke="#1A1A1A" strokeWidth="1" data-id="element-273" />
        <path d="M -15 12 Q 0 8, 15 12" fill="none" stroke="#1A1A1A" strokeWidth="1" data-id="element-274" />
        <text x="0" y="42" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fontWeight="600" fill="#4A4A4A" data-id="element-275">
          BRAIN
        </text>
      </g>

      {/* Bladder (bottom) */}
      <g transform="translate(80, 200)" data-id="element-276">
        <path d="M -28 -10 C -28 -22, -20 -28, 0 -28 C 20 -28, 28 -22, 28 -10 C 28 5, 20 18, 0 18 C -20 18, -28 5, -28 -10 Z" fill="none" stroke="#1A1A1A" strokeWidth="1.5" data-id="element-277" />
        <path d="M -3 -28 L -3 -36 M 3 -28 L 3 -36" stroke="#1A1A1A" strokeWidth="1.5" data-id="element-278" />
        <text x="0" y="38" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fontWeight="600" fill="#4A4A4A" data-id="element-279">
          BLADDER
        </text>
      </g>

      {/* Nerve pathway (left → connecting brain to bladder) */}
      <g data-id="element-280">
        <path d="M 80 60 Q 100 130, 80 175" stroke="#8E3A5A" strokeWidth="1.5" strokeDasharray="3 3" fill="none" data-id="element-281" />
        {/* Signal pulses along the path */}
        <circle cx="92" cy="100" r="3" fill="#8E3A5A" data-id="element-282" />
        <circle cx="98" cy="130" r="3" fill="#8E3A5A" data-id="element-283" />
        <circle cx="92" cy="160" r="3" fill="#8E3A5A" data-id="element-284" />
      </g>

      {/* Vertical divider */}
      <line x1="200" y1="20" x2="200" y2="240" stroke="#E8E0D5" strokeWidth="1" data-id="element-285" />

      {/* Right side: Pelvic floor diagram */}
      <g transform="translate(300, 130)" data-id="element-286">
        {/* Pelvis bowl outline */}
        <path d="M -70 -50 Q -75 -20, -55 0 L -55 20 Q -55 35, -35 35 L 35 35 Q 55 35, 55 20 L 55 0 Q 75 -20, 70 -50" fill="none" stroke="#1A1A1A" strokeWidth="1.5" data-id="element-287" />
        {/* Pelvic floor muscle (hammock) */}
        <path d="M -45 5 Q 0 30, 45 5" stroke="#8E3A5A" strokeWidth="3" fill="none" strokeLinecap="round" data-id="element-288" />
        {/* Muscle texture lines */}
        <path d="M -40 8 Q 0 28, 40 8" stroke="#8E3A5A" strokeWidth="1" fill="none" opacity="0.5" data-id="element-289" />
        <path d="M -35 11 Q 0 26, 35 11" stroke="#8E3A5A" strokeWidth="1" fill="none" opacity="0.4" data-id="element-290" />

        {/* Signal pulse arrow into the muscle */}
        <g transform="translate(-65, -10)" data-id="element-291">
          <path d="M 0 0 L 12 0 M 8 -3 L 12 0 L 8 3" stroke="#C9A96E" strokeWidth="1.5" fill="none" data-id="element-292" />
        </g>

        <text x="0" y="60" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fontWeight="600" fill="#4A4A4A" data-id="element-293">
          PELVIC FLOOR
        </text>
        <text x="0" y="74" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="9" fill="#4A4A4A" data-id="element-294">
          muscle &amp; nerve junction
        </text>
      </g>

      {/* Labels */}
      <text x="80" y="125" textAnchor="middle" fontFamily="Playfair Display, serif" fontStyle="italic" fontSize="11" fill="#8E3A5A" data-id="element-295">
        the signal
      </text>
    </svg>;
}
// === PRESS LOGOS — masthead-style typographic logos ===
export function PressLogos({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`flex flex-wrap items-center justify-center gap-x-8 gap-y-3 ${className}`} data-id="element-296">
      {/* The Telegraph */}
      <div className="flex flex-col items-center text-secondary opacity-60" data-id="element-297">
        <span className="text-[8px] font-inter uppercase tracking-[0.25em] -mb-0.5" data-id="element-298">
          The
        </span>
        <span className="font-playfair font-bold text-[18px] tracking-tight leading-none" data-id="element-299">
          Telegraph
        </span>
      </div>
      {/* Saga */}
      <div className="text-secondary opacity-60" data-id="element-300">
        <span className="font-playfair italic font-bold text-[22px] tracking-tight" data-id="element-301">
          Saga
        </span>
      </div>
      {/* Daily Mail */}
      <div className="text-secondary opacity-60" data-id="element-302">
        <span className="font-playfair font-black text-[16px] tracking-tighter uppercase" data-id="element-303">
          Daily Mail
        </span>
      </div>
      {/* Good Housekeeping */}
      <div className="flex flex-col items-center text-secondary opacity-60" data-id="element-304">
        <span className="font-playfair font-bold text-[12px] tracking-tight leading-tight uppercase" data-id="element-305">
          Good
        </span>
        <span className="font-playfair font-bold text-[12px] tracking-tight leading-tight uppercase" data-id="element-306">
          Housekeeping
        </span>
      </div>
      {/* Woman & Home */}
      <div className="text-secondary opacity-60" data-id="element-307">
        <span className="font-playfair italic font-semibold text-[13px] tracking-wide" data-id="element-308">
          Woman&amp;Home
        </span>
      </div>
    </div>;
}
// === GUARANTEE SEAL — concentric medallion with starburst ===
export function GuaranteeSeal({
  className = ''
}: {
  className?: string;
}) {
  // Generate starburst tick marks around the rim
  const ticks = Array.from({
    length: 36
  }, (_, i) => i * 10);
  return <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true" data-id="element-309">
      {/* Outer rim with starburst ticks */}
      <g data-id="element-310">
        {ticks.map(angle => <line key={angle} x1="100" y1="6" x2="100" y2="12" stroke="#C9A96E" strokeWidth="1" transform={`rotate(${angle} 100 100)`} data-id="element-311" />)}
      </g>

      {/* Outer circle */}
      <circle cx="100" cy="100" r="92" fill="#8E3A5A" stroke="#C9A96E" strokeWidth="2" data-id="element-312" />

      {/* Inner ring */}
      <circle cx="100" cy="100" r="84" fill="none" stroke="#C9A96E" strokeWidth="0.5" opacity="0.6" data-id="element-313" />

      {/* Curved top text path */}
      <defs data-id="element-314">
        <path id="topArc" d="M 30 100 A 70 70 0 0 1 170 100" fill="none" data-id="element-315" />
        <path id="bottomArc" d="M 30 100 A 70 70 0 0 0 170 100" fill="none" data-id="element-316" />
      </defs>
      <text fill="#C9A96E" fontFamily="Inter, sans-serif" fontSize="10" fontWeight="600" letterSpacing="3" data-id="element-317">
        <textPath href="#topArc" startOffset="50%" textAnchor="middle" data-id="element-318">
          MONEY-BACK GUARANTEE
        </textPath>
      </text>
      <text fill="#C9A96E" fontFamily="Inter, sans-serif" fontSize="9" fontWeight="500" letterSpacing="3" data-id="element-319">
        <textPath href="#bottomArc" startOffset="50%" textAnchor="middle" data-id="element-320">
          NO QUESTIONS ASKED
        </textPath>
      </text>

      {/* Center "90" */}
      <text x="100" y="98" textAnchor="middle" fontFamily="Playfair Display, serif" fontWeight="700" fontSize="52" fill="white" data-id="element-321">
        90
      </text>
      <text x="100" y="120" textAnchor="middle" fontFamily="Inter, sans-serif" fontWeight="600" fontSize="11" letterSpacing="3" fill="#C9A96E" data-id="element-322">
        DAYS
      </text>

      {/* Decorative laurel-style flourishes left & right */}
      <g stroke="#C9A96E" strokeWidth="1" fill="none" opacity="0.6" data-id="element-323">
        <path d="M 38 100 Q 32 92, 40 84" data-id="element-324" />
        <path d="M 38 100 Q 32 108, 40 116" data-id="element-325" />
        <path d="M 162 100 Q 168 92, 160 84" data-id="element-326" />
        <path d="M 162 100 Q 168 108, 160 116" data-id="element-327" />
      </g>
    </svg>;
}
// === BENEFIT ICONS — for ProductReveal benefit list ===
export function BenefitIcon({
  type,
  className = '',
  color = '#C9A96E'
}: {
  type: 'transdermal' | 'sleep' | 'no-routine' | 'clean' | 'women' | 'fast';
  className?: string;
  color?: string;
}) {
  const props = {
    fill: 'none',
    stroke: color,
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const
  };
  return <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true" data-id="element-328">
      {type === 'transdermal' && <g {...props} data-id="element-329">
          <rect x="3" y="9" width="18" height="6" rx="1" data-id="element-330" />
          <circle cx="12" cy="12" r="1.5" fill={color} data-id="element-331" />
          <path d="M 7 9 L 7 7 M 17 9 L 17 7 M 7 17 L 7 15 M 17 17 L 17 15" data-id="element-332" />
        </g>}
      {type === 'sleep' && <g {...props} data-id="element-333">
          <path d="M 20 14 A 8 8 0 1 1 10 4 A 6 6 0 0 0 20 14 Z" data-id="element-334" />
        </g>}
      {type === 'no-routine' && <g {...props} data-id="element-335">
          <circle cx="12" cy="12" r="9" data-id="element-336" />
          <path d="M 12 7 L 12 12 L 15 14" data-id="element-337" />
        </g>}
      {type === 'clean' && <g {...props} data-id="element-338">
          <path d="M 12 3 L 12 8 M 9 5 L 9 8 M 15 5 L 15 8" data-id="element-339" />
          <path d="M 7 8 L 17 8 L 16 20 A 1 1 0 0 1 15 21 L 9 21 A 1 1 0 0 1 8 20 Z" data-id="element-340" />
        </g>}
      {type === 'women' && <g {...props} data-id="element-341">
          <circle cx="12" cy="8" r="4" data-id="element-342" />
          <path d="M 12 12 L 12 18 M 9 16 L 15 16" data-id="element-343" />
        </g>}
      {type === 'fast' && <g {...props} data-id="element-344">
          <path d="M 13 3 L 4 14 L 11 14 L 11 21 L 20 10 L 13 10 Z" data-id="element-345" />
        </g>}
    </svg>;
}
// === NUMBERED BADGE for timeline ===
export function NumberBadge({
  number,
  variant = 'default'
}: {
  number: number;
  variant?: 'default' | 'final';
}) {
  const isfinal = variant === 'final';
  return <div className={`w-full h-full rounded-full flex items-center justify-center font-playfair font-bold text-[14px] ${isfinal ? 'bg-forest text-gold' : 'bg-white text-forest'}`} data-id="element-346">
      {String(number).padStart(2, '0')}
    </div>;
}